import { Link } from "@tanstack/react-router";
import { PanelLeftClose, PanelLeftOpen, Sparkles } from "lucide-react";

import { Logo } from "@/app/Logo";
import type { NavGroup } from "@/app/registry";
import { useSession } from "@/app/session";
import { Tooltip } from "@/components/ui";
import { cn } from "@/lib/cn";

import { useRoleCard } from "./roleCard";
import { highlighted, useSectionLabels, useSections } from "./sections";

/** Menu labels in the side menu (the admin page is the admin console here). */
function itemLabel(path: string, label: string): string {
  return path === "/app/admin" ? "Admin console" : label;
}

/**
 * The side menu: the signed-in user's sections and pages on a dark panel.
 * `rail` shows icons only (collapsed, or inside the admin console where the
 * console's own menu sits beside it).
 */
export function Sidebar({
  rail,
  onToggle,
  canToggle = true,
}: {
  rail: boolean;
  onToggle: () => void;
  canToggle?: boolean;
}) {
  const sections = useSections();
  const labels = useSectionLabels();
  const { user } = useSession();
  const card = useRoleCard();
  const initial = (user?.display_name || user?.email || "?")
    .slice(0, 1)
    .toUpperCase();

  return (
    <aside
      aria-label="Main menu"
      className={cn(
        "hidden shrink-0 bg-[#0c1638] lg:block",
        rail ? "w-[76px]" : "w-64",
      )}
      data-theme="dark"
    >
      <div className="sticky top-[var(--safe-top)] flex h-safe flex-col bg-gradient-to-b from-[#0a1230] to-[#0c1638] text-[#c6cfeb]">
        <div
          className={cn(
            "flex h-16 items-center",
            rail ? "justify-center" : "justify-between pr-2 pl-4",
          )}
        >
          <Link to="/app" aria-label="Home">
            <Logo withName={!rail} className="[&>span]:text-white" />
          </Link>
          {canToggle && !rail ? (
            <Tooltip content="Collapse menu">
              <button
                type="button"
                onClick={onToggle}
                aria-label="Collapse menu"
                className="grid size-8 place-items-center rounded-lg text-[#97a3c7] hover:bg-white/10 hover:text-white"
              >
                <PanelLeftClose className="size-4" />
              </button>
            </Tooltip>
          ) : null}
        </div>

        {!rail ? (
          <div className="mx-3.5 mb-2 flex items-center gap-2.5 rounded-xl border border-[#1e2a58] bg-[#111b42] px-3 py-2.5 text-xs">
            <span
              className={cn(
                "size-2 shrink-0 rounded-full",
                card.active
                  ? "bg-[#3ccf98] shadow-[0_0_0_3px_rgb(60_207_152/0.18)]"
                  : "bg-[#f87171] shadow-[0_0_0_3px_rgb(248_113_113/0.18)]",
              )}
            />
            <div className="min-w-0">
              <div className="truncate font-semibold text-white">
                {card.title}
              </div>
              {card.sub ? (
                <div className="truncate text-[11px] text-[#97a3c7]">
                  {card.sub}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        <nav aria-label="Main" className="flex-1 overflow-y-auto px-2.5 pb-3 [scrollbar-width:thin]">
          {[...sections.entries()].map(([group, entries], gi) => (
            <div
              key={group}
              className={cn(
                "mt-3",
                rail && gi > 0 && "border-t border-[#1e2a58] pt-2",
              )}
            >
              {!rail ? (
                <div
                  className={cn(
                    "mb-1 flex items-center gap-1.5 px-2 text-[10.5px] font-semibold tracking-[0.09em] uppercase",
                    highlighted(entries) ? "text-[#c4b5ff]" : "text-[#97a3c7]",
                  )}
                >
                  {highlighted(entries) ? <Sparkles className="size-3" aria-hidden /> : null}
                  <span className="truncate">{labels[group as NavGroup]}</span>
                </div>
              ) : null}
              <ul className="grid gap-0.5">
                {entries.map((p) => {
                  const Icon = p.nav.icon;
                  const Count = p.nav.badge;
                  const label = itemLabel(p.path, p.nav.label);
                  const link = (
                    <Link
                      to={p.path}
                      aria-label={rail ? label : undefined}
                      className={cn(
                        "relative flex h-9 items-center gap-3 rounded-[10px] text-[13.5px] transition-colors hover:bg-white/[0.07] hover:text-white",
                        rail ? "justify-center" : "px-2.5",
                        p.nav.highlight && "bg-[#7c5cff]/[0.14] font-medium text-white",
                      )}
                      activeProps={{
                        className:
                          "bg-gradient-to-r from-[#3b74ff]/30 to-[#3b74ff]/[0.06] !text-white font-semibold before:absolute before:-left-2.5 before:inset-y-2 before:w-[3px] before:rounded before:bg-[#6f9bff]",
                        "aria-current": "page",
                      }}
                    >
                      <Icon
                        className={cn(
                          "shrink-0 opacity-90",
                          rail ? "size-[19px]" : "size-[17px]",
                          p.nav.highlight && "text-[#c4b5ff] opacity-100",
                        )}
                      />
                      {!rail ? <span className="truncate">{label}</span> : null}
                      {!rail && Count ? (
                        <span className="ml-auto">
                          <Count />
                        </span>
                      ) : null}
                    </Link>
                  );
                  return (
                    <li key={p.path}>
                      {rail ? (
                        <Tooltip content={label} side="right">
                          {link}
                        </Tooltip>
                      ) : (
                        link
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div
          className={cn(
            "flex items-center gap-2.5 border-t border-[#1e2a58] p-3",
            rail && "flex-col",
          )}
        >
          <span className="grid size-8 shrink-0 place-items-center rounded-full bg-gradient-to-br from-[#6f9bff] to-[#a08bff] text-sm font-bold text-[#08101f]">
            {initial}
          </span>
          {!rail ? (
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-white">
                {user?.display_name || user?.email}
              </div>
              <div className="truncate text-[11.5px] text-[#97a3c7]">
                {card.title}
              </div>
            </div>
          ) : null}
          {canToggle && rail ? (
            <Tooltip content="Expand menu" side="right">
              <button
                type="button"
                onClick={onToggle}
                aria-label="Expand menu"
                className="grid size-8 place-items-center rounded-lg text-[#97a3c7] hover:bg-white/10 hover:text-white"
              >
                <PanelLeftOpen className="size-4" />
              </button>
            </Tooltip>
          ) : null}
        </div>
      </div>
    </aside>
  );
}
